import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteReplacement,
  apiGetAllReplacements,
  apiGetDevicesInformation
} from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import {
  communityList,
  convertLocaltimeToUTC,
  downloadBlobFile,
  formatCurrency
} from '@utils/utils';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { ADMIN_ROLE } from '@utils/constant';
import { TYPES } from '../Add/components/Add';
import { useRole } from '@contexts/Auth/UseRole';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const { RangePicker } = DatePicker;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const [dateRange, setDateRange] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const { isWebAdmin, isWebManager, isWebSuperAdmin } = useRole();
  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllReplacements({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        serialNo: serialNo ?? undefined,
        fromDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
          : undefined,
        toDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
          : undefined
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/replacement/update/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/replacement/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá sự kiện thay thế này?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteReplacement(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        render: (text, record) => {
          return (
            <>
              <span style={{ color: '#0174CF' }}>{record?.customer_id}</span> -{' '}
              {record?.customer_name}
            </>
          );
        },
        width: 200
      },
      {
        title: 'Loại',
        dataIndex: 'type',
        fixed: 'left',
        render: (text, record) => {
          return <>{TYPES?.find((item) => item.value === record?.type)?.label}</>;
        },
        width: 150
      },
      {
        title: 'Thời điểm thực hiện',
        dataIndex: 'replacement_date',
        render: (text, record) => {
          return <>{moment(record?.replacement_date).utc().format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 100
      },
      {
        title: 'Module cũ',
        children: [
          {
            title: 'Số Serial',
            dataIndex: 'serial_no_old',
            width: 100
          },
          {
            title: 'Model',
            dataIndex: 'model_old',
            width: 100
          },
          {
            title: 'Chỉ số cũ (L)',
            dataIndex: 'meter_reading_old',
            render: (_, record) => (
              <>{record?.meter_reading_old ? formatCurrency(record?.meter_reading_old) : 'N/A'}</>
            ),
            width: 130
          }
        ]
      },
      {
        title: 'Module mới',
        children: [
          {
            title: 'Số Serial',
            dataIndex: 'serial_no_new',
            width: 100
          },
          {
            title: 'Model',
            dataIndex: 'model_new',
            width: 100
          },
          {
            title: 'Chỉ số mới (L)',
            dataIndex: 'meter_reading_new',
            render: (_, record) => (
              <>{record?.meter_reading_new ? formatCurrency(record?.meter_reading_new) : 'N/A'}</>
            ),
            width: 140
          }
        ]
      },
      {
        title: 'Ghi chú',
        dataIndex: 'reason',
        width: 200
      },
      {
        title: 'Hình ảnh',
        dataIndex: 'image',
        width: 200,
        render: (_, record) => (
          <div>
            {record.old_device_image?.url && (
              <div>
                <a
                  href={record.old_device_image.url} // Use the URL from the object
                  target="_blank" // Open the link in a new tab
                  rel="noopener noreferrer" // Security best practice
                  style={{ color: '#0174CF', textDecoration: 'underline' }}>
                  Ảnh thiết bị cũ
                </a>
              </div>
            )}

            {record.meter_reading_old_image?.url && (
              <div>
                <a
                  href={record.meter_reading_old_image.url} // Use the URL from the object
                  target="_blank" // Open the link in a new tab
                  rel="noopener noreferrer" // Security best practice
                  style={{ color: '#0174CF', textDecoration: 'underline' }}>
                  Ảnh chỉ số cũ
                </a>
              </div>
            )}
            {record.new_device_image?.url && (
              <div>
                <a
                  href={record.new_device_image.url} // Use the URL from the object
                  target="_blank" // Open the link in a new tab
                  rel="noopener noreferrer" // Security best practice
                  style={{ color: '#0174CF', textDecoration: 'underline' }}>
                  Ảnh thiết bị mới
                </a>
              </div>
            )}
            {record.meter_reading_new_image?.url && (
              <div>
                <a
                  href={record.meter_reading_new_image.url} // Use the URL from the object
                  target="_blank" // Open the link in a new tab
                  rel="noopener noreferrer" // Security best practice
                  style={{ color: '#0174CF', textDecoration: 'underline' }}>
                  Ảnh chỉ số mới
                </a>
              </div>
            )}
          </div>
        )
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {(isWebAdmin || isWebManager || isWebSuperAdmin) && (
              <>
                <Tooltip title="Chỉnh sửa">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
                <Tooltip title="Xoá">
                  <Button
                    type="danger"
                    shape="circle"
                    icon={<DeleteFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const onChangeDateRange = (dates, dateStrings) => {
    setDateRange(dates);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/replacements/export-report`,
        'Danh sách thông tin thay thế.xlsx',
        {
          keyWord: search ?? undefined,
          serialNo: serialNo ?? undefined,
          fromDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
            : undefined,
          toDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
            : undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    if (user?.user?.role !== ADMIN_ROLE) {
      showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
      return;
    }
    navigate('/replacement/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Quản lý thay thế lắp mới
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddDevice}
              style={{
                color: '#0174CF',
                height: '36px',
                backgroundColor: '#E8EBEF',
                borderColor: '#E8EBEF',
                fontSize: '18px'
              }}>
              Thêm mới sự kiện
            </Button>
            {(isWebAdmin || isWebManager || isWebSuperAdmin) && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}

            <Button
              icon={<UploadOutlined />}
              onClick={() => {
                if (user?.user?.role !== ADMIN_ROLE) {
                  showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
                  return;
                }
                setVisibleModalImport(true);
              }}
              style={{
                color: '#fff',
                height: '36px',
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                fontSize: '18px'
              }}>
              Nhập file excel
            </Button>
          </Space>
        </Col>
      </Row>
      <Card>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <Select
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              style={{ width: '100%', height: '36px' }}>
              {communityList.map((community) => (
                <Option key={community.value} value={community.value}>
                  {community.label}
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col md={6} xs={12}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col>
          <Col md={6} xs={12}>
            <Input
              style={{ height: '36px', width: '100%', fontSize: '18px' }}
              size="small"
              placeholder="Tìm kiếm theo số serial, imei..."
              value={serialNo}
              onChange={handleSearchSerialNoChange}
            />
          </Col>
          <Col md={6} sm={12}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col md={6} sm={12}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          // size="middle"
          loading={isFetching}
          dataSource={deviceList || []}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            onChange: handleChangePage,
            onShowSizeChange: handleChangePageSize,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          style={{
            borderRadius: '4px',
            thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
            fontSize: '30px'
          }}
          scroll={{ x: 'max-content', y: 600 }}
        />
      </Card>
    </div>
  );
}
